<template>
    <teleport to="body">
        <transition name="speaker-fade">
            <div v-if="speaker" class="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6"
                role="dialog" aria-modal="true">
                <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="$emit('close')"></div>

                <div
                    class="relative z-10 w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-app bg-body shadow-2xl">
                    <!-- Close -->
                    <button type="button" @click="$emit('close')"
                        class="absolute top-4 right-4 z-20 p-1.5 rounded-lg bg-body/70 backdrop-blur text-body hover:text-header transition-colors">
                        <X class="h-5 w-5" />
                    </button>

                    <!-- Header -->
                    <div class="relative">
                        <div class="h-28 bg-gradient-to-br from-brand-500/90 via-primary-500/60 to-brand-500/25"></div>
                        <div class="px-6 -mt-12 flex items-center gap-4">
                            <div
                                class="h-24 w-24 shrink-0 bg-card rounded-2xl overflow-hidden border-4 border-body bg-brand-500/10 flex items-center justify-center shadow-lg">
                                <img v-if="speaker.photoUrl" :src="speaker.photoUrl" :alt="speaker.name"
                                    class="h-full w-full object-cover" />
                                <span v-else class="text-brand-500 font-black text-2xl">{{ initials(speaker.name) }}</span>
                            </div>
                            <div class="min-w-0 pb-1">
                                <h3 class="text-header font-bold text-xl leading-snug truncate mb-3">{{ speaker.name }}</h3>
                                <p v-if="speaker.role || speaker.company"
                                    class="text-body/80 text-sm font-semibold truncate">
                                    {{ [speaker.role, speaker.company].filter(Boolean).join(' · ') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Body -->
                    <div class="px-6 py-6 space-y-6">
                        <!-- Socials -->
                        <div v-if="speaker.socials && speaker.socials.length" class="flex flex-wrap gap-2">
                            <a v-for="(s, i) in speaker.socials" :key="i" :href="s.url" target="_blank" rel="noopener"
                                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-app bg-card/70 text-sm font-semibold text-header hover:border-brand-500/40 hover:text-brand-500 transition-colors">
                                <component :is="socialIcon(s.platform)" class="h-4 w-4" />
                                {{ socialLabel(s.platform) }}
                            </a>
                        </div>

                        <!-- Topic -->
                        <div v-if="speaker.topic" class="rounded-2xl border border-app bg-brand-500/5 p-4">
                            <p class="text-xs font-bold text-brand-600 uppercase tracking-widest mb-1">Speaking on</p>
                            <p class="text-header font-semibold">“{{ speaker.topic }}”</p>
                        </div>

                        <!-- Bio -->
                        <div v-if="speaker.bio">
                            <p class="text-xs font-bold text-body/80 uppercase tracking-widest mb-2">Biography</p>
                            <div class="text-body text-sm leading-relaxed whitespace-pre-line" v-html="speaker.bio"></div>
                        </div>

                        <p v-if="!speaker.bio && !speaker.topic && !(speaker.socials && speaker.socials.length)"
                            class="text-body/80 text-sm">No additional details available.</p>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script setup lang="ts">
import { watch, onBeforeUnmount } from 'vue';
import { X } from 'lucide-vue-next';
import { EventSpeaker } from '~/types';
import { socialIcon, socialLabel } from './helpers';

const props = defineProps<{ speaker: EventSpeaker | null }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const initials = (name: string) =>
    (name || '')
        .split(' ')
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();

const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') emit('close');
};

watch(
    () => props.speaker,
    (s) => {
        if (s) {
            document.body.classList.add('modal-open');
            window.addEventListener('keydown', onKey);
        } else {
            document.body.classList.remove('modal-open');
            window.removeEventListener('keydown', onKey);
        }
    }
);

onBeforeUnmount(() => {
    document.body.classList.remove('modal-open');
    window.removeEventListener('keydown', onKey);
});
</script>

<style scoped>
.bg-body {
    background-color: rgb(var(--core-body-bg));
}

.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

.text-body {
    color: rgb(var(--core-body-text));
}

.speaker-fade-enter-active,
.speaker-fade-leave-active {
    transition: opacity 0.2s ease;
}

.speaker-fade-enter-from,
.speaker-fade-leave-to {
    opacity: 0;
}
</style>
